import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  onVisibleAreaChange,
  placeInView,
  touchForm,
  typingInside,
} from './keepOpenWhileTyping.js';

/**
 * 316 — the shared behaviour of the schedule's confirm popovers (the shell `MoveBookingPopover` and
 * `CreateBlockPopover` already implement): placed from an anchor rectangle via `placePopover`,
 * re-placed when it resizes, dismissed on Escape, an outside press, a scroll or a window resize (404: on a
 * touch screen a pop-up with a field is re-placed above the keyboard instead of closing).
 */
export function usePopoverShell(anchorRect: DOMRect, onDismiss: () => void) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const place = () => {
      const { width, height } = el.getBoundingClientRect();
      setPlacement(placeInView(anchorRect, { width, height }));
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    // 404 (#1329): the keyboard opening or closing moves the visible area; follow it.
    const stop = onVisibleAreaChange(place);
    return () => {
      ro.disconnect();
      stop();
    };
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    // 404 (#1329): on a touch screen, a pop-up with a field is not closed by the keyboard's resizes and scrolls (it
    // is re-placed above it), and the first tap outside while typing only closes the keyboard.
    const onDown = (e: PointerEvent) => {
      if (typingInside(boxRef.current)) return;
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onDismiss();
    };
    const close = () => {
      if (!touchForm(boxRef.current)) onDismiss();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onDismiss]);

  const style = {
    position: 'fixed' as const,
    left: placement?.left ?? anchorRect.left,
    top: placement?.top ?? anchorRect.bottom + 8,
    visibility: placement ? ('visible' as const) : ('hidden' as const),
    zIndex: 50,
  };
  return { boxRef, style, placed: placement !== null };
}
